import { useNavigate } from "react-router";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Spinner } from "@/components/common/svgs";
import { Search } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { cn } from "@/utils/css";
import { hasDigits, useQuery } from "@/utils/query";
import { protectedRoutes } from "@/routing";
import { setCurrentSearch } from "@/state/currentSearchSlice";
import { today, tomorrow } from "@/utils/date";
import { defaultRadiusType } from "@/utils/constants";

export const SearchButton = () => {
    const navigate = useNavigate();
    const {
        buttonFocus,
        pickup,
        delivery,
        pickupDate,
        pickupDateEnd,
        deliveryDate,
        deliveryDateEnd,
        trailerType,
        searchType,
        pickupQuery,
        deliveryQuery,
        pickupRadius,
        deliveryRadius,
    } = useSelector((state) => state.searchInput);
    const buttonRef = useRef(null);
    const { outboundRatio, outboundDuration, volume, withLoads, extendedMarket } = useQuery();
    const dispatch = useDispatch();

    const isLoadRate = searchType === "load_rates";
    const isLoadboard = searchType === "loadboard";

    const loading = useSelector(
        (state) =>
            state.loadRates.ratePrediction.loading ||
            state.loadRates.marketToMarketRoute.loading ||
            state.rateHistory.loading ||
            state.marketPotential.loading ||
            state.bestOutbounds.loading,
        //TODO check if this is all loading
    );

    const disabled = !trailerType || !pickup || (isLoadRate && !delivery) || loading;

    const queryParams = new URLSearchParams({
        trailerType: trailerType?.id,
        pickupZip: pickup?.zip,
        pickupPlaceName: pickup?.placeName,
        pickupStateCode: pickup?.stateCode,
        pickupWithZip: hasDigits(pickupQuery),
        ...(isLoadRate || (isLoadboard && delivery)
            ? {
                  deliveryZip: delivery?.zip || "",
                  deliveryPlaceName: delivery?.placeName || "",
                  deliveryStateCode: delivery?.stateCode || "",
                  deliveryWithZip: hasDigits(deliveryQuery),
              }
            : {}),
        ...(!isLoadboard || (isLoadboard && pickupDate) ? { pickupDate: pickupDate || today } : {}),
        ...(!isLoadboard || (isLoadboard && pickupDateEnd) ? { pickupDateEnd: pickupDateEnd || "" } : {}),
        ...(isLoadboard && deliveryDate ? { deliveryDate: deliveryDate || tomorrow } : {}),
        ...(isLoadboard && deliveryDateEnd ? { deliveryDateEnd: deliveryDateEnd || "" } : {}),
        ...(outboundRatio ? { outboundRatio } : {}),
        ...(volume ? { volume } : {}),
        ...(outboundDuration ? { outboundDuration } : {}),
        ...{ withLoads: withLoads ? "true" : "false" },
        ...{ extendedMarket: extendedMarket ? "true" : "false" },
        ...{ pickupRadius: pickupRadius?.id || defaultRadiusType.id },
        ...{ deliveryRadius: deliveryRadius?.id || defaultRadiusType.id },
    }).toString();

    const to = `${isLoadRate ? protectedRoutes[1].path : isLoadboard ? protectedRoutes[6].path : protectedRoutes[2].path}?${queryParams}`;

    useEffect(() => {
        if (buttonRef.current) {
            buttonRef.current.focus();
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: "smooth",
            });
        }
    }, [buttonFocus]);

    const handleClick = useCallback(
        (e) => {
            if (disabled || loading) {
                e.preventDefault();
            } else {
                dispatch(
                    setCurrentSearch({
                        trailerType,
                        pickup,
                        pickupQuery,
                        delivery: isLoadRate || (isLoadboard && delivery) ? delivery : null,
                        deliveryQuery: isLoadRate || (isLoadboard && delivery) ? deliveryQuery : null,
                        pickupDate: !isLoadboard || (isLoadboard && pickupDate) ? pickupDate : null,
                        pickupDateEnd: !isLoadboard || (isLoadboard && pickupDateEnd) ? pickupDateEnd : null,
                        deliveryDate: isLoadboard && deliveryDate ? deliveryDate : null,
                        deliveryDateEnd: isLoadboard && deliveryDateEnd ? deliveryDateEnd : null,
                        pickupRadius,
                        deliveryRadius,
                        searchType,
                    }),
                );
                navigate(to);
            }
        },
        [
            disabled,
            loading,
            pickup,
            delivery,
            pickupDate,
            pickupDateEnd,
            deliveryDate,
            deliveryDateEnd,
            trailerType,
            searchType,
            pickupRadius,
            deliveryRadius,
        ],
    );

    return (
        <button
            ref={buttonRef}
            className={cn(
                "w-full h-full min-h-[49px] md:w-[78px] md:h-[64px] rounded-lg inline-flex items-center justify-center bg-cm-accent",
                {
                    "bg-cm-accent-800 opacity-50": disabled,
                },
            )}
            onClick={handleClick}
            disabled={!!disabled}
        >
            {loading ? <Spinner className="w-5 h-5" /> : <Search className="w-5 h-5 text-cm-text-on-accent" />}
        </button>
    );
};
